import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, MemoryRouter } from "react-router-dom";
import { I18nProvider } from "@/i18n/I18nProvider";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { useSettingsStore } from "@/data/settingsStore";
import { usePartiesStore } from "@/data/partiesStore";
import { usePresentationsStore } from "@/data/presentationsStore";
import React, { Suspense, lazy, useEffect } from 'react';
import ScrollToTop from "@/components/ScrollToTop";
import GlobalAdBanner from "@/components/GlobalAdBanner";
import AdPopupModal from "@/components/AdPopupModal";
import ChatBotWidget from "@/components/ChatBotWidget";
import SystemBubblesManager from "@/components/SystemBubblesManager";
import CitizenBadgesPanel from "@/components/CitizenBadgesPanel";
import SoftLaunchGuard from "@/components/SoftLaunchGuard";
import ComingSoonPage from "./pages/ComingSoonPage";
import Index from "./pages/Index";
const DashboardLayout = lazy(() => import('./pages/DashboardLayout'));
const DashboardIndex = lazy(() => import('./pages/DashboardIndex'));
import CitizenAlertsPage from "./pages/CitizenAlertsPage";
const PartyDashboardPage = lazy(() => import('./pages/PartyDashboardPage'));
import VoterPage from "./pages/VoterPage";
import RingPage from "./pages/RingPage";
import EngagementPage from "./pages/EngagementPage";
import ObservateurPage from "./pages/ObservateurPage";
import PourquoiPage from "./pages/PourquoiPage";
import ResourceDetailPage from "./pages/ResourceDetailPage";
import ActionsDetailPage from "./pages/ActionsDetailPage";
import ProposerEvenementPage from "./pages/ProposerEvenementPage";
import ProposerEmissionPage from "./pages/ProposerEmissionPage";
import ProposerPublicitePage from "./pages/ProposerPublicitePage";
import CandidatesResultPage from "./pages/CandidatesResultPage";
import AllEventsPage from "./pages/AllEventsPage";
import AdminAdsPage from "./pages/AdminAdsPage";
import AdminEventsPage from "./pages/AdminEventsPage";
import AdminOffersPage from "./pages/AdminOffersPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import AdminEngagementsPage from "./pages/AdminEngagementsPage";
import AdminPartisPage from "./pages/AdminPartisPage";
import AdminInteractionsMessagesPage from "./pages/AdminInteractionsMessagesPage";
import AdminEspacesDediesPage from "./pages/AdminEspacesDediesPage";
import EvenementDetailPage from "./pages/EvenementDetailPage";
import PartiDetailPage from "./pages/PartiDetailPage";
import InstitutionDetailPage from "./pages/InstitutionDetailPage";
import QuizPage from "./pages/QuizPage";
import AdminCandidatesPage from "./pages/AdminCandidatesPage";
import AdminFinancePage from "./pages/AdminFinancePage";
import AdminAnalyticsPage from "./pages/AdminAnalyticsPage";
import AdminQuizPage from "./pages/AdminQuizPage";
import CandidacyInfoPage from "./pages/CandidacyInfoPage";
import AllEmissionsPage from "./pages/AllEmissionsPage";
import EmissionDetailPage from "./pages/EmissionDetailPage";
import AdminEmissionsPage from "./pages/AdminEmissionsPage";
import MediaDashboardPage from "./pages/MediaDashboardPage";
import AdminContentPage from "./pages/AdminContentPage";
import AdminMapPage from "./pages/AdminMapPage";
import AdminNavbarPage from "./pages/AdminNavbarPage";
import AdminFooterPage from "./pages/AdminFooterPage";
import AdminBullesPage from "./pages/AdminBullesPage";
import AdminTalkPage from "./pages/AdminTalkPage";
import AdminHeroStatsPage from "./pages/AdminHeroStatsPage";
import AdminPrioritiesPage from "./pages/AdminPrioritiesPage";
import AdminAlertsPage from "./pages/AdminAlertsPage";
import AdminChatbotPage from "./pages/AdminChatbotPage";
import AdminElectionResultsPage from "./pages/AdminElectionResultsPage";
import AdminTransparencePage from "./pages/AdminTransparencePage";
import AdminInscriptionsPage from "./pages/AdminInscriptionsPage";
import AdminAdminsPage from "./pages/AdminAdminsPage";
import AdminNewsletterPage from "./pages/AdminNewsletterPage";
import AdminPresentationsPage from "./pages/AdminPresentationsPage";
import TalkPage from "./pages/TalkPage";
import TalkArchivesPage from "./pages/TalkArchivesPage";
import MentionsLegalesPage from "./pages/MentionsLegalesPage";
import ConditionsPage from "./pages/ConditionsPage";
import ConfidentialitePage from "./pages/ConfidentialitePage";
import MediathequePage from "./pages/MediathequePage";
import AdminMatchPage from "./pages/AdminMatchPage";
import AdminRingPage from "./pages/AdminRingPage";
import AdminSuperControlCenterPage from "./pages/AdminSuperControlCenterPage";
import AdminSpotNewsPage from "./pages/AdminSpotNewsPage";
import MatchPage from "./pages/MatchPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import AdminLoginPage from "./pages/AdminLoginPage";
import NotFound from "./pages/NotFound";
import OrgEventsPage from "./pages/OrgEventsPage";
import PartnerProfilePage from "./pages/PartnerProfilePage";

const queryClient = new QueryClient();

function getDashboardRoutes() {
  return (
  <Route path="/dashboard" element={<Suspense fallback={<div className="h-screen w-full flex items-center justify-center">Chargement de l'interface sécurisée...</div>}><DashboardLayout /></Suspense>}>
    <Route path="alertes" element={<CitizenAlertsPage />} />
    <Route index element={<Suspense fallback={<div>Chargement...</div>}><DashboardIndex /></Suspense>} />
    <Route path="candidats" element={<Suspense fallback={<div>Chargement...</div>}><PartyDashboardPage /></Suspense>} />
    <Route path="utilisateurs" element={<AdminUsersPage />} />
    <Route path="engagements" element={<AdminEngagementsPage />} />
    <Route path="partis" element={<AdminPartisPage />} />
    <Route path="espaces-dedies" element={<AdminEspacesDediesPage />} />
    <Route path="partis-interactions" element={<AdminInteractionsMessagesPage />} />
    <Route path="media" element={<MediaDashboardPage />} />
    <Route path="mes-evenements" element={<OrgEventsPage />} />
    <Route path="profil" element={<PartnerProfilePage />} />
    <Route path="emissions" element={<AdminEmissionsPage />} />
    <Route path="evenements-partis" element={<AdminEventsPage eventType="partis" />} />
    <Route path="evenements-citoyens" element={<AdminEventsPage eventType="citoyens" />} />
    <Route path="publicites" element={<AdminAdsPage />} />
    <Route path="admin-candidats" element={<AdminCandidatesPage />} />
    <Route path="presentations" element={<AdminPresentationsPage />} />
    <Route path="finances" element={<AdminFinancePage />} />
    <Route path="analytics" element={<AdminAnalyticsPage />} />
    <Route path="navbar" element={<AdminNavbarPage />} />
    <Route path="footer" element={<AdminFooterPage />} />
    <Route path="carte" element={<AdminMapPage />} />
    <Route path="bulles" element={<AdminBullesPage />} />
    <Route path="chatbot" element={<AdminChatbotPage />} />
    <Route path="talk" element={<AdminTalkPage />} />
    <Route path="priorites" element={<AdminPrioritiesPage />} />
    <Route path="contenu" element={<AdminContentPage />} />
    <Route path="spot-news" element={<AdminSpotNewsPage />} />
    <Route path="quiz" element={<AdminQuizPage />} />
    <Route path="stat-navbar" element={<AdminHeroStatsPage />} />
    <Route path="nuit-electorale" element={<AdminElectionResultsPage />} />
    <Route path="admin-alertes" element={<AdminAlertsPage />} />
    <Route path="transparence" element={<AdminTransparencePage />} />
    <Route path="offres" element={<AdminOffersPage />} />
    <Route path="inscriptions" element={<AdminInscriptionsPage />} />
    <Route path="newsletter" element={<AdminNewsletterPage />} />
    <Route path="admins" element={<AdminAdminsPage />} />
    <Route path="match" element={<AdminMatchPage />} />
    <Route path="ring" element={<AdminRingPage />} />
    <Route path="super-controle" element={<AdminSuperControlCenterPage />} />
  </Route>
  );
}

function App() {
  const siteTitle = useSettingsStore(state => state.getSetting('kaynin_site_title', "Kayn'in_كاينين_ⴽⴰⵢⵏⵉⵏ"));
  const fetchParties = usePartiesStore(state => state.fetchProfiles);
  const fetchPresentations = usePresentationsStore(state => state.fetchPresentations);

  useEffect(() => {
    document.title = siteTitle as string;
  }, [siteTitle]);

  useEffect(() => {
    fetchParties();
    fetchPresentations();
  }, [fetchParties, fetchPresentations]);

  return (
    <AuthProvider>
      <QueryClientProvider client={queryClient}>
        <I18nProvider>
          <TooltipProvider>
            <BrowserRouter>
              <Toaster />
              <Sonner />
                <ScrollToTop />
                <AdPopupModal />
                <div className="fixed bottom-4 right-4 z-50 w-full max-w-sm md:bottom-6 md:right-6">
                  <GlobalAdBanner />
                </div>
                <Routes>
                  <Route path="/coming-soon" element={<ComingSoonPage />} />
                  <Route path="/in" element={<AdminLoginPage />} />
                  <Route path="/contact" element={<Navigate to="/" replace />} />
                  <Route path="/login" element={<Navigate to="/" replace />} />
                  <Route path="/register" element={<Navigate to="/" replace />} />
                  <Route path="/" element={<Index />} />
                  <Route path="/partis" element={<Index />} />
                  <Route path="/actions" element={<Index />} />
                  <Route path="/agenda-national" element={<Index />} />
                  <Route path="/institutions" element={<Index />} />
                  <Route path="/ressources" element={<Index />} />
                  <Route path="/attentes-citoyennes" element={<Index />} />
                  <Route path="/barometres" element={<Index />} />
                  <Route path="/accueil-emissions" element={<Index />} />
                  <Route path="/accueil-quiz" element={<Index />} />
                  <Route path="/accueil-pourquoi" element={<Index />} />
                  <Route path="/accueil-talk" element={<Index />} />
                  <Route path="/accueil-evenements" element={<Index />} />
                  
                  <Route path="/pitcher" element={<Index />} />
                  <Route path="/poster" element={<Index />} />
                  <Route path="/scroller" element={<Index />} />
                  <Route path="/watcher" element={<Index />} />
                  <Route path="/politiquer" element={<Index />} />
                  <Route path="/logguer" element={<Index />} />
                  <Route path="/voter" element={<SoftLaunchGuard><VoterPage /></SoftLaunchGuard>} />
                  <Route path="/ring" element={<SoftLaunchGuard><RingPage /></SoftLaunchGuard>} />
                  <Route path="/engagement" element={<SoftLaunchGuard><EngagementPage /></SoftLaunchGuard>} />
                  <Route path="/observateur" element={<SoftLaunchGuard><ObservateurPage /></SoftLaunchGuard>} />
                  <Route path="/pourquoi" element={<SoftLaunchGuard><PourquoiPage /></SoftLaunchGuard>} />
                  <Route path="/resultats-candidats" element={<SoftLaunchGuard><CandidatesResultPage /></SoftLaunchGuard>} />
                  <Route path="/ressources/:slug" element={<SoftLaunchGuard><ResourceDetailPage /></SoftLaunchGuard>} />
                  <Route path="/actions/:slug" element={<SoftLaunchGuard><ActionsDetailPage /></SoftLaunchGuard>} />
                  <Route path="/proposer-evenement" element={<SoftLaunchGuard><ProposerEvenementPage /></SoftLaunchGuard>} />
                  <Route path="/proposer-emission" element={<SoftLaunchGuard><ProposerEmissionPage /></SoftLaunchGuard>} />
                  <Route path="/proposer-publicite" element={<SoftLaunchGuard><ProposerPublicitePage /></SoftLaunchGuard>} />
                  <Route path="/evenement/:id" element={<SoftLaunchGuard><EvenementDetailPage /></SoftLaunchGuard>} />
                  <Route path="/evenements/:type" element={<SoftLaunchGuard><AllEventsPage /></SoftLaunchGuard>} />
                  <Route path="/emissions" element={<SoftLaunchGuard><AllEmissionsPage /></SoftLaunchGuard>} />
                  <Route path="/emissions/:id" element={<SoftLaunchGuard><EmissionDetailPage /></SoftLaunchGuard>} />
                  <Route path="/mediatheque" element={<SoftLaunchGuard><MediathequePage /></SoftLaunchGuard>} />
                  <Route path="/parti/:acronym" element={<SoftLaunchGuard><PartiDetailPage /></SoftLaunchGuard>} />
                  <Route path="/institutions/:id" element={<SoftLaunchGuard><InstitutionDetailPage /></SoftLaunchGuard>} />
                  <Route path="/publicite" element={<Navigate to="/proposer-publicite" replace />} />
                  <Route path="/quiz" element={<SoftLaunchGuard><QuizPage /></SoftLaunchGuard>} />
                  <Route path="/match" element={<SoftLaunchGuard><MatchPage /></SoftLaunchGuard>} />
                  <Route path="/candidat-info" element={<SoftLaunchGuard><CandidacyInfoPage /></SoftLaunchGuard>} />
                  <Route path="/talk" element={<SoftLaunchGuard><TalkPage /></SoftLaunchGuard>} />
                  <Route path="/talk/archives" element={<SoftLaunchGuard><TalkArchivesPage /></SoftLaunchGuard>} />
                  <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
                  <Route path="/conditions-utilisation" element={<ConditionsPage />} />
                  <Route path="/politique-confidentialite" element={<ConfidentialitePage />} />
                  
                  {/* Dashboard Routes for Partners / B2B */}
                  {getDashboardRoutes()}
                  
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
                <ChatBotWidget />
                <SystemBubblesManager />
                <CitizenBadgesPanel />
            </BrowserRouter>
          </TooltipProvider>
        </I18nProvider>
      </QueryClientProvider>
    </AuthProvider>
  );
};

export default App;
